// Pick task types, modelled on cms1 (pickTask + pickHistory + pickSource + pickIssue),
// surfaced through the BFF on :4101.

export enum PickTaskStatus {
  New = 'new', // created, nothing scanned/picked yet
  InProgress = 'in-progress', // items scanned/picked, not yet fully shipped
  Closed = 'closed', // all items picked and shipped
  Cancelled = 'cancelled',
}

// How a pick task was created. Auto-generated origins (e.g. the replenishment
// report's Location Name action) let the picker scan any good stock across the
// source location's warehouse hierarchy; 'manual' tasks are bound to their
// chosen source locations.
export enum PickTaskOrigin {
  Manual = 'manual',
  ReplenishmentReport = 'replenishment-report',
}

export interface PickSource {
  id: number;
  pickHistoryId: number;
  locationNodeId: number;
  locationName?: string;
  partOwnerId?: number | null;
  conditionCode?: string | null;
  quantity: number;
}

export interface PickHistory {
  id: number;
  pickTaskId: number;
  partTypeId: number;
  partTypeName?: string;
  // Presigned URL of the part type's main photo, resolved by the BFF.
  partTypeImageUrl?: string;
  // Non-serialised part type: picked by quantity rather than by scanning serials.
  isConsumable?: boolean;
  skuId?: number | null;
  conditionCode?: string | null;
  partOwnerId?: number | null;
  toLocationNodeId: number;
  toLocationName?: string;
  qtyRequired: number;
  qtyMin: number;
  qtyStock: number;
  qtyTopUp: number;
  qtyPending: number;
  qtyBackOrder: number;
  qtyShipped: number;
  lastUnderAt?: string | null;
  sources?: PickSource[];
  availableSerials?: AvailableSerial[];
  // Per-location good-stock counts for this line (serialised parts counted one
  // each, non-serialised by quantity). Drives the "Pick from" breakdown.
  availableLocations?: AvailableLocation[];
  // Roll-up of this part type's replenishment picture across every location that
  // stocks it, versus the stock available at this task's source. Drives the
  // stock-shortage note in the pick item header.
  replenishmentSummary?: ReplenishmentSummary;
}

// How the part type's total top-up need (across all its locations) compares with
// the stock available at this task's source location. Computed by the BFF.
export interface ReplenishmentSummary {
  // Locations that still need topping up (current stock below their Recommended max).
  locationCount: number;
  // Sum of each location's Recommended top-up: Σ max(0, max - stock).
  totalRecommendedTopUp: number;
  // Good stock available for this part type at this task's source location.
  sourceAvailable: number;
  // How far the source falls short of the total top-up: max(0, total - available).
  shortfall: number;
  // Up to 5 other locations under their Recommended level, Amtek depots first,
  // excluding this line's own destination. Drives the "other locations" column.
  otherLocations?: OtherLocationNeed[];
}

// One other location that also needs this part type, with how much it requires to
// reach its Required (min) and Recommended (max) levels.
export interface OtherLocationNeed {
  locationNodeId: number;
  locationName: string;
  // True when the location name identifies an Amtek depot; these are listed first.
  isAmtek: boolean;
  // Units needed to reach the location's Required (min) level: max(0, min - stock).
  requiredToMin: number;
  // Units needed to reach the location's Recommended (max) level: max(0, max - stock).
  recommendedTopUp: number;
}

export interface AvailableLocation {
  locationNodeId: number;
  locationName?: string;
  available: number;
}

export interface AvailableSerial {
  partId: number;
  serial1: string;
  locationNodeId: number;
  locationName?: string;
}

export interface PickIssue {
  id: number;
  pickTaskId: number;
  shipmentId?: number | null;
  partTypeId?: number | null;
  quantity: number;
  notes?: string | null;
  createdAt: string;
}

export interface PickTaskShipment {
  id: number;
  shipmentStatusCode?: string;
  shipmentTypeCode?: string;
  pickTaskId?: number | null;
  createdAt?: string;
}

export interface PickTask {
  id: number;
  origin?: PickTaskOrigin | string;
  sourceReplenishmentConfigId?: number | null;
  fromLocationNodeId?: number | null;
  fromLocationName?: string;
  fromLocationTypeCode?: string | null;
  fromLocationTypeName?: string;
  toLocationNodeId: number;
  toLocationName?: string;
  toLocationTypeCode?: string | null;
  toLocationTypeName?: string;
  allocatedToUserId?: number | null;
  allocatedToUserName?: string;
  allocatedBy?: number | null;
  allocatedAt?: string | null;
  // Optional due date: a dispatch shipment should happen by this date. Drives the
  // in-app "due soon/today/overdue" reminders shown to the allocated user.
  sendBy?: string | null;
  status: PickTaskStatus | string;
  sentAt?: string | null;
  sentAllAt?: string | null;
  receivedAt?: string | null;
  receivedAllAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  cancelledBy?: number | null;
  cancelledByName?: string;
  createdByName?: string;
  priority?: string | null;
  note?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: number;
  history?: PickHistory[];
  issues?: PickIssue[];
  shipments?: PickTaskShipment[];
  pickedSerials?: PickedSerial[] | null;
  pickedConsumables?: PickedConsumable[] | null;
}

// A serial scanned during picking, persisted on the task and carried into the
// dispatch manifest when the task is completed.
export interface PickedSerial {
  partTypeId: number;
  partId: number;
  serial1: string;
}

// A picked quantity for a non-serialised (consumable) part type, taken from a
// specific source location. Persisted on the task and carried into the dispatch
// manifest when the task is completed.
export interface PickedConsumable {
  partTypeId: number;
  locationNodeId: number;
  locationName?: string;
  quantity: number;
}

// ---- prepare / create ----

export interface PreparedSource {
  locationNodeId: number;
  locationName?: string;
  parentLocationName?: string;
  available?: number;
  conditionCode: string;
  quantity: number;
}

export interface PreparedItem {
  partTypeId: number;
  partTypeName?: string;
  skuId?: number;
  conditionCode: string;
  toLocationNodeId: number;
  qtyRequired: number;
  qtyMin: number;
  qtyStock: number;
  qtyTopUp: number;
  qtyPending: number;
  qtyBackOrder: number;
  sources: PreparedSource[];
}

export interface PreparedTask {
  toLocationNodeId: number;
  toLocationName?: string;
  fromLocationNodeId?: number | null;
  fromLocationName?: string;
  sourceReplenishmentConfigIds: number[];
  items: PreparedItem[];
}

export interface PrepareRequest {
  replenishmentConfigIds: number[];
  condition?: string;
  // number = pick from this node; null = no source (blank); omitted = default to the user's parent node.
  fromLocationNodeId?: number | null;
}

export interface CreatePickTaskRequest {
  allocatedToUserId?: number;
  fromLocationNodeId?: number | null;
  toLocationNodeId: number;
  sourceReplenishmentConfigIds?: number[];
  // Defaults to 'manual' on the BFF when omitted (the Create Pick Task modal).
  origin?: PickTaskOrigin | string;
  // Optional due date (YYYY-MM-DD): a dispatch shipment should happen by this date.
  sendBy?: string;
  priority?: string;
  note?: string;
  items: PreparedItem[];
}

export interface PickTaskListFilters {
  allocatedToUserId?: number;
  status?: string;
  toLocationNodeId?: number;
}

export interface PickConfig {
  defaultSourceNodeId?: number | null;
  defaultSourceNodeName?: string;
}
